import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Building as BuildingIcon,
  MapPin as MapPinIcon,
  PencilSimple as PencilSimpleIcon,
  Trash as TrashIcon,
} from '@phosphor-icons/react/dist/ssr';
import { useMutation } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import dayjs from 'dayjs';

import type { Unit } from '@/types/v2/unit';
import { deleteUnit } from '@/api/units';
import { paths } from '@/paths';
import { useDialog } from '@/hooks/v2/use-dialog';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { RoleGuard } from '@/components/auth/guards/role-guard';
import { EditUnitDialog } from './edit-unit-dialog';

interface UnitSidebarProps {
  unit?: Unit | null;
  isLoading: boolean;
  onRefresh?: () => void;
}

function SectionLabel({ icon, children, action }: { icon?: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {icon && <Box sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled' }}>
          {children}
        </Typography>
      </Stack>
      {action}
    </Stack>
  );
}

function KVRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" sx={{ py: 1.25, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', flexShrink: 0, minWidth: 90 }} variant="body2">
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, textAlign: 'right' }} variant="body2">
        {children}
      </Typography>
    </Stack>
  );
}

export function UnitSidebar({ unit, isLoading, onRefresh }: UnitSidebarProps): React.JSX.Element {
  const editDialog = useDialog();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUnit(id),
    onSuccess: () => {
      toast.success('Unit deleted successfully');
      navigate(paths.units.index);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete unit');
    },
  });

  const handleDelete = () => {
    confirm({
      title: 'Delete Unit',
      description: 'Are you sure? All unit assignments, history, and data will be permanently removed.',
      confirmationText: 'Delete Unit',
      confirmationButtonProps: { color: 'error', variant: 'contained' },
    })
      .then(async () => {
        if (unit) await deleteMutation.mutateAsync(unit.uuid ?? unit.id.toString());
      })
      .catch(() => {});
  };

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={18} variant="text" />
        ))}
      </Stack>
    );
  }

  return (
    <>
      <Stack divider={<Divider />} spacing={0}>
        {/* ── Property details ── */}
        <Box sx={{ pb: 2 }}>
          <SectionLabel
            icon={<BuildingIcon size={14} />}
            action={
              <IconButton onClick={editDialog.handleOpen} size="small" sx={{ color: 'text.disabled' }}>
                <PencilSimpleIcon size={14} />
              </IconButton>
            }
          >
            Property Details
          </SectionLabel>
          <Stack sx={{ mt: 1 }} divider={<Divider flexItem />}>
            {unit?.address && (
              <KVRow label="Address">{unit.address}</KVRow>
            )}
            {unit?.unit_number && (
              <KVRow label="Unit #">{unit.unit_number}</KVRow>
            )}
            {(unit?.city || unit?.state) && (
              <KVRow label="Location">
                {[unit?.city, unit?.state, unit?.zip_code].filter(Boolean).join(', ')}
              </KVRow>
            )}
            {unit?.floor && (
              <KVRow label="Floor">{unit.floor}</KVRow>
            )}
            <KVRow label="Type">
              {unit?.is_rental ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    px: 1,
                    py: '2px',
                    borderRadius: 1,
                    bgcolor: '#6366F118',
                    border: '1px solid #6366F140',
                  }}
                >
                  <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#6366F1', lineHeight: 1.4 }}>
                    Rental
                  </Typography>
                </Box>
              ) : (
                'Owner-occupied'
              )}
            </KVRow>
            {unit?.unit_type?.name && (
              <KVRow label="Unit Type">{unit.unit_type.name}</KVRow>
            )}
            {unit?.created_at && (
              <KVRow label="Created">{dayjs(unit.created_at).format('MMM D, YYYY')}</KVRow>
            )}
          </Stack>
        </Box>

        {/* ── Location ── */}
        {(unit?.city || unit?.state) && (
          <Box sx={{ py: 2 }}>
            <SectionLabel icon={<MapPinIcon size={14} />}>Full Address</SectionLabel>
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                {unit?.address}
                {unit?.unit_number ? `, Unit ${unit.unit_number}` : ''}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                {[unit?.city, unit?.state, unit?.zip_code].filter(Boolean).join(', ')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Actions ── */}
        <Box sx={{ py: 2 }}>
          <SectionLabel>Actions</SectionLabel>
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <Button
              fullWidth
              onClick={editDialog.handleOpen}
              size="small"
              startIcon={<PencilSimpleIcon size={14} />}
              variant="outlined"
            >
              Edit Unit
            </Button>
            <RoleGuard
              requiredAccountRoles={[ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER]}
              requiredCommunityRoles={[COMMUNITY_ROLES.COMMUNITY_MANAGER]}
            >
              <Button
                color="error"
                disabled={deleteMutation.isPending}
                fullWidth
                onClick={handleDelete}
                size="small"
                startIcon={<TrashIcon size={14} />}
                variant="outlined"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete Unit'}
              </Button>
            </RoleGuard>
          </Stack>
        </Box>
      </Stack>

      {unit && (
        <EditUnitDialog
          onClose={editDialog.handleClose}
          onSuccess={() => onRefresh?.()}
          open={editDialog.open}
          unit={unit}
        />
      )}
    </>
  );
}
